import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID } from '~/_shared/constants/debugConstants';
import { chunk } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { AppErrorType } from '~/appError/appErrorType.enum';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { QUICK_EDIT_BOUNDARY_COUNT_THRESHOLD } from '~/boundary/editMode/boundaryEditModal/constants';
import { getOverlappingBoundaries } from '~/boundary/editMode/boundaryEditSubmitModal/boundaryEditSubmit.utils';
import type { BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  boundaryItemsUpdateItem, boundaryItemsUpdateItems,
} from '~/store/boundaryItems/boundaryItems.actionCreators';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  buildGeometryCalculations, convertBoundaryCalculationsToGeometryCalculations,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.utils';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { type Geometry } from '../../../_shared/types/polygon/polygon.types';
import {
  type BoundaryCombineGeometryRequest,
  type BoundaryCombineGeometryRequestItem, type BoundaryItemResponseBase,
  type BulkBoundaryCombineGeometryCalculationRequest,
  bulkUpdateBoundaryGeometry,
  getBoundaryCombineGeometry, getBulkBoundaryCombineGeometry,
  type UpdateBoundaryRequestModel,
} from '../../../store/boundaries/boundaries.repository';
import { createBoundaryStateItemFromBoundaryResponse } from '../../../store/boundaryItems/boundaryItems.factory';
import { type BoundaryStateItem } from '../../../store/boundaryItems/boundaryItems.state';
import { useClientIdSelector } from '../../../store/selectors/useClientIdSelector';
import { type BoundaryEditSubmitOption } from './boundaryEditSubmitModal.component';
import { BoundaryEditSubmitModalAction } from './boundaryEditSubmitModalAction.enum';

type UseBoundaryEditActionsProps = {
  editedBoundaryTerritoryGroupId: number;
};

export const useBoundaryEditActions = ({ editedBoundaryTerritoryGroupId }: UseBoundaryEditActionsProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector() ?? undefined;
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const boundaryItems = useMapBoundariesSelector();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();

  const editedBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(editedBoundaryTerritoryGroupId);
  const editedBoundaryGroupId = editedBoundaryTerritoryGroup?.boundaryGroupId;

  const boundaryCombineGeometry = useCallback(async (request: BoundaryCombineGeometryRequest) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }

    return await getBoundaryCombineGeometry(clientId, request);
  }, [clientId]);

  const reassignGeometry = useCallback(async (
    targetBoundaryId: BoundaryId,
    geometry: BoundaryCombineGeometryRequestItem[],
  ) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }
    if (!mapId) {
      throw Error('Missing mapId');
    }

    if (!editedBoundaryGroupId) {
      return { newBoundaryStateItems: [], lastEtag: null };
    }

    const allBoundaries = boundaryItems.get(editedBoundaryGroupId)?.values().toArray();
    const targetBoundary = allBoundaries?.find((boundary) => boundary.id === targetBoundaryId);

    const newBoundaryStateItems: BoundaryStateItem[] = [];
    let lastEtag: BoundaryItemResponseBase['etag'] | null = null;
    try {
      const { boundaries: overlappingBoundaries } = await getOverlappingBoundaries(clientId, mapId, geometry, editedBoundaryGroupId);
      const boundariesToEdit = [
        ...overlappingBoundaries,
        targetBoundary,
      ].filter(notNullsy);
      const boundaryChunks = chunk(boundariesToEdit, QUICK_EDIT_BOUNDARY_COUNT_THRESHOLD);
      const combineRemovedBoundariesRequest: BulkBoundaryCombineGeometryCalculationRequest = {
        base: geometry,
        include: [],
        subtract: [],
      };
      let boundaryGeometryResults: Awaited<ReturnType<typeof getBulkBoundaryCombineGeometry>>['results'] = {};
      for (const boundaryChunk of boundaryChunks) {
        const calculations = {
          ...buildGeometryCalculations(
            {
              boundaryGroupId: editedBoundaryGroupId,
              boundaryIds: boundaryChunk.map(boundary => 'boundary_id' in boundary ? boundary.boundary_id : boundary.id),
            },
            { type: 'query', query_id: 'removals' },
            { [targetBoundaryId]: { include: geometry, subtract: [] } }
          ),
          removals: combineRemovedBoundariesRequest,
        };

        const geometryResponse = await getBulkBoundaryCombineGeometry(
          clientId,
          DEBUG_SEND_GEOMETRY_INSTEAD_OF_BOUNDARY_ID ? convertBoundaryCalculationsToGeometryCalculations(calculations, boundaryItems) : calculations,
        );

        boundaryGeometryResults = {
          ...boundaryGeometryResults,
          ...geometryResponse.results,
        };
      }

      if (Object.values(boundaryGeometryResults).some(result => !result.success)) {
        dispatch(createAppError({
          type: AppErrorType.General,
          errorTitle: t('An error occurred while calculating new territory geometry.'),
          title: t('Boundary Update Error'),
        }));
        return null;
      }

      const boundaryUpdateRequests = Object.entries(boundaryGeometryResults)
        .map(([stringBoundaryId, geometryResult]) => {
          const boundaryId = Number(stringBoundaryId);
          const boundary = allBoundaries?.find(({ id }) => id === boundaryId);

          if (!boundary || !geometryResult.geometry) {
            return null;
          }

          return {
            boundary_id: boundaryId,
            geometry: geometryResult.geometry,
            map_id: mapId,
          } satisfies UpdateBoundaryRequestModel;
        }).filter(notNullsy);

      const boundaryUpdateRequestChunks = chunk(boundaryUpdateRequests, QUICK_EDIT_BOUNDARY_COUNT_THRESHOLD);

      for (const boundaryUpdateRequestChunk of boundaryUpdateRequestChunks) {
        const updatedBoundaries: Awaited<ReturnType<typeof bulkUpdateBoundaryGeometry>> =
          await bulkUpdateBoundaryGeometry(clientId, boundaryUpdateRequestChunk);
        updatedBoundaries.forEach((updatedBoundary) => {
          newBoundaryStateItems.push(createBoundaryStateItemFromBoundaryResponse(updatedBoundary).boundaryStateItem);
          lastEtag = updatedBoundary.etag;
        });
      }

    }
    catch (e) {
      // skip updates with error
      console.error('Error while updating boundary geometry', e);
      return null;
    }
    return { newBoundaryStateItems, lastEtag };
  }, [boundaryItems, clientId, dispatch, editedBoundaryGroupId, mapId, t]);

  const updateBoundary = useCallback(async (boundaryId: number, newGeometry: Geometry) => {
    if (!clientId) {
      throw Error('Missing clientId');
    }

    const response = await bulkUpdateBoundaryGeometry(clientId, [{
      boundary_id: boundaryId,
      geometry: newGeometry,
      map_id: mapId,
    }]);

    if (!response[0]) {
      throw new Error('Failed to update boundary geometry');
    }

    return createBoundaryStateItemFromBoundaryResponse(response[0], { enforceLabelBoundsRecalc: true });
  }, [clientId, mapId]);

  const editBoundary = useCallback(async (
    submitData: BoundaryEditSubmitOption,
    baseGeometry: BoundaryCombineGeometryRequestItem[],
    geometry: BoundaryCombineGeometryRequestItem[],
    _onSuccess: () => void, onError: () => void
  ) => {
    if (!editedBoundaryGroupId) {
      return;
    }

    setIsLoading(true);

    const onSuccess = (boundaryStateItem: BoundaryStateItem, etag: BoundaryGroup['etag']) => {
      dispatch(boundaryItemsUpdateItem(
        editedBoundaryGroupId,
        boundaryStateItem,
        etag
      ));
      setIsLoading(false);
      _onSuccess();
    };

    const onBulkSuccess = (boundaryStateItems: BoundaryStateItem[], etag?: BoundaryGroup['etag']) => {
      dispatch(boundaryItemsUpdateItems(
        editedBoundaryGroupId,
        boundaryStateItems,
        etag,
      ));
      setIsLoading(false);
      _onSuccess();
    };

    try {
      if (submitData.action === BoundaryEditSubmitModalAction.AddToExistingTerritory) {
        const newGeometry = await boundaryCombineGeometry({
          include: [...baseGeometry, ...geometry],
          subtract: [],
        });

        updateBoundary(submitData.selectedBoundaryId, newGeometry?.geometry)
          .then((boundaryItemAndEtag) =>
            onSuccess(boundaryItemAndEtag.boundaryStateItem, boundaryItemAndEtag.etag)
          );
      }

      if (submitData.action === BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory) {
        const newGeometry = await boundaryCombineGeometry({
          include: baseGeometry,
          subtract: geometry,
        });

        updateBoundary(submitData.selectedBoundaryId, newGeometry?.geometry)
          .then((boundaryItemAndEtag) =>
            onSuccess(boundaryItemAndEtag.boundaryStateItem, boundaryItemAndEtag.etag)
          );
      }

      if (submitData.action === BoundaryEditSubmitModalAction.ReassignTerritory) {

        const newBoundaryStateItems = await reassignGeometry(submitData.targetBoundaryId, geometry);
        if (newBoundaryStateItems) {
          onBulkSuccess(
            newBoundaryStateItems.newBoundaryStateItems,
            newBoundaryStateItems.lastEtag ?? undefined
          );
        }
      }
    }
    catch (e) {
      console.error('Error while editing boundary', e);
      setIsLoading(false);
      onError();
    }
  }, [boundaryCombineGeometry, reassignGeometry, dispatch, editedBoundaryGroupId, updateBoundary]);

  return {
    isLoading,
    editBoundary,
  };
};
